
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ClientFormValues } from "../schema/form-schema";

interface UseClientFormSubmitProps {
  mode: 'create' | 'edit';
  clientId?: string;
  onSuccess: () => void;
}

export const useClientFormSubmit = ({ mode, clientId, onSuccess }: UseClientFormSubmitProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSchoolIdEditable, setIsSchoolIdEditable] = useState(true);

  useEffect(() => {
    const checkSchoolUsers = async () => {
      if (mode === 'edit' && clientId) {
        const { data: currentClient } = await supabase
          .from('clients')
          .select('school_id')
          .eq('id', clientId)
          .single();

        if (currentClient) {
          const { data, error } = await supabase
            .from('school_users')
            .select('id')
            .eq('school_id', currentClient.school_id)
            .limit(1);

          if (error) {
            console.error('Error checking school users:', error);
            setIsSchoolIdEditable(false);
            return;
          }

          setIsSchoolIdEditable(!(data && data.length > 0));
        }
      }
    };

    checkSchoolUsers();
  }, [mode, clientId]);

  const handleSubmit = async (data: ClientFormValues) => {
    try {
      setIsLoading(true);
      
      const clientData = {
        school_id: data.school_id,
        nama_sekolah: data.nama_sekolah,
      };

      if (mode === 'create') {
        const { error } = await supabase.from('clients').insert([clientData]);
        if (error) {
          if (error.code === '23505') {
            toast({
              title: "Error",
              description: "School ID already exists. Please use a different School ID.",
              variant: "destructive",
            });
            return false;
          }
          throw error;
        }
      } else {
        const updateData = { ...clientData };
        if (!isSchoolIdEditable) {
          delete updateData.school_id;
        }
        
        const { error } = await supabase
          .from('clients')
          .update(updateData)
          .eq('id', clientId);
        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `Client ${mode === 'create' ? 'created' : 'updated'} successfully`,
      });

      return true;
    } catch (error) {
      console.error("Error saving client:", error);
      toast({
        title: "Error",
        description: `Failed to ${mode} client`,
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleSubmit,
    isLoading,
    isSchoolIdEditable,
  };
};
